/**
 * Broadcast — compose and send an email blast to an audience. Super-admin only
 * (also gated server-side). A deliberate, confirm-before-send flow.
 */

import React, { useState } from 'react';
import {
  Box, Paper, TextField, MenuItem, Typography, Stack, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress, useTheme,
} from '@mui/material';
import { Campaign, Send } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { PageHeader, panelSx, ButtonPill } from '../ui';

const AUDIENCES = [
  { value: 'active_subscribers', label: 'Active newsletter subscribers' },
  { value: 'all_customers', label: 'All active customers' },
];

const Broadcast = () => {
  const theme = useTheme();
  const [form, setForm] = useState({ subject: '', body: '', audience: 'active_subscribers' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const valid = form.subject.trim().length > 2 && form.body.trim().length > 5;

  const send = async () => {
    try {
      setSending(true);
      const res = await adminService.broadcast(form);
      setConfirmOpen(false);
      setSnackbar({ open: true, message: `Queued to ${res.queued} recipient${res.queued !== 1 ? 's' : ''}.`, severity: 'success' });
      setForm({ subject: '', body: '', audience: form.audience });
    } catch (err) {
      setConfirmOpen(false);
      setSnackbar({ open: true, message: err?.message ?? 'Broadcast failed', severity: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <PageHeader title="Broadcast" subtitle="Send an announcement to your audience." />

      <Paper elevation={0} sx={{ ...panelSx(theme), maxWidth: 720 }}>
        <Stack spacing={2.5}>
          <Alert severity="warning" icon={<Campaign />}>
            This sends a real email to everyone in the selected audience. Double-check before sending.
          </Alert>
          <TextField
            select label="Audience" value={form.audience}
            onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
          >
            {AUDIENCES.map((a) => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
          </TextField>
          <TextField
            label="Subject" value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            fullWidth
          />
          <TextField
            label="Message" value={form.body}
            onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
            fullWidth multiline rows={8}
            placeholder="Write your announcement…"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonPill variant="contained" startIcon={<Send />} disabled={!valid} onClick={() => setConfirmOpen(true)}>
              Review &amp; send
            </ButtonPill>
          </Box>
        </Stack>
      </Paper>

      <Dialog open={confirmOpen} onClose={() => !sending && setConfirmOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Send this broadcast?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To: <strong>{AUDIENCES.find((a) => a.value === form.audience)?.label}</strong>
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: theme.palette.grey[50] }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>{form.subject}</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{form.body}</Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending} sx={{ borderRadius: 9999 }}>Cancel</Button>
          <ButtonPill variant="contained" onClick={send} disabled={sending} startIcon={sending ? <CircularProgress size={18} /> : <Send />}>
            {sending ? 'Sending…' : 'Send now'}
          </ButtonPill>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Broadcast;
